import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone } from "@fortawesome/free-regular-svg-icons/faClone";
import { faFile } from "@fortawesome/free-regular-svg-icons/faFile";
import {
  type TableProps,
  type TablePaginationConfig,
  Badge,
  MenuProps,
} from "../../utils/local-antd";
import isNil from "lodash/isNil";
import minBy from "lodash/minBy";
import omitBy from "lodash/omitBy";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import { Flex } from "../../components/Flex";
import { SubHeader } from "../../components/SubHeader";
import { WarningModal } from "../../components/WarningModal";
import { Entry } from "../../model/entry/Entry";
import { GetLatestEntriesFilterDto } from "../../model/entry/dto/GetLatestEntriesFilterDto";
import { UpdateEntryDto } from "../../model/entry/dto/UpdateEntryDto";
import { PageMeta } from "../../model/meta/PageMeta";
import { UserRoles } from "../../model/user/types/UserRoles";
import { entrySliceSelectors } from "../../redux/entry/entry.selector";
import { entryActions } from "../../redux/entry/entry.slice";
import { tagActions } from "../../redux/tag/tag.slice";
import { formSliceSelectors } from "../../redux/forms/form.selector";
import { formActions } from "../../redux/forms/form.slice";
import { integrationSliceSelectors } from "../../redux/integration/integration.selector";
import { integrationActions } from "../../redux/integration/integration.slice";
import { kanbanSliceSelectors } from "../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../redux/kanban/kanban.slice";
import { permissionSliceSelectors } from "../../redux/permission/permission.selector";
import { permissionActions } from "../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { TestIDs } from "../../tests/types/testIds";
import { Spacing } from "../../theme/Spacing";
import { AtiraToast } from "../../utils/AtiraToast";
import { truncateString } from "../../utils/String";
import { EntryTableBulkActionsHeader } from "./components/EntryTableBulkActionsHeader";
import { EntryTableHeader } from "./components/EntryTableHeader";
import { EntryTableSettingsDrawer } from "./components/tableSettings/EntryTableSettingsDrawer";
import { EntiresTableWithDND } from "./components/table/EntriesTableWithDND";
import { createEntriesTableColumns } from "./utils/createEntriesTableColumns";
import { moduleActions } from "../../redux/module/module.slice";
import { DynamicModuleInstanceCreateDrawer } from "../modules/module-instance/DynamicModuleInstanceCreateDrawer";
import { ModuleDefinition } from "../../model/module/types/module-definition/ModuleDefinition";
import { moduleSliceSelectors } from "../../redux/module/module.selector";
import { EntriesDuplicatesResolveDrawer } from "./components/EntriesDuplicatesResolveDrawer";
import { EntryDuplicateBulkInitiateResolveDrawer } from "./components/EntryDuplicateBulkInitiateResolveDrawer";
import { entryCreateEditDuplicateErrorHandler } from "./utils/entryCreateEditDuplicateErrorHandler";
import { taskActions } from "../../redux/task/task.slice";
import { ModuleInstanceAttachmentCreateModal } from "../modules/module-instance-attachment/ModuleInstanceAttachmentCreateModal";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { useDrawersContext } from "../tasks/drawers-context";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { EntityIcons } from "../../utils/GlobalIcons";
import { faTableColumns } from "@fortawesome/free-solid-svg-icons/faTableColumns";
import { ExportImportEntriesModal } from "./components/ExportImportEntriesModal";
import { AtiraDropdown } from "../../components/AtiraDropdown";
import { Button } from "../../components/Button";
import { AtiraIcon } from "../../components/AtiraIcon";
import { Text } from "../../components/Text";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { Form } from "../../model/form/Form";

const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;
const FIND_DUPLICATES_TOAST_KEY = "find-duplicates";

const Wrapper = styled(Flex)`
  flex-direction: column;
  padding: 0 ${Spacing.m};
  flex: 1;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
`;

const EntriesRoute: React.FC = () => {
  const [tableSettingsDrawerVisible, setTableSettingsDrawerVisible] =
    useState(false);
  const [deleteTaskLoading, setDeleteTaskLoading] = useState(false);
  const [taskDoneLoading, setTaskDoneLoading] = useState(false);
  const [moduleInstanceCreateVisible, setModuleInstanceCreateVisible] =
    useState(false);
  const [selectedModuleDefinition, setSelectedModuleDefinition] =
    useState<ModuleDefinition | null>(null);
  const [
    fixedModuleInstanceAttachToEntryModalVisible,
    setFixedModuleInstanceAttachToEntryModalVisible,
  ] = useState(false);
  const [findDuplicatesLoading, setFindDuplicatesLoading] = useState(false);
  const [whatsappChatLoading, setWhatsappChatLoading] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formMethods = useForm<GetLatestEntriesFilterDto>({
    defaultValues: {
      formId: undefined,
      kind: undefined,
      range: undefined,
      priority: undefined,
      keyword: "",
    },
  });
  const { t } = useTranslation();
  const { state } = useLocation();
  const theme = useTheme();
  const {
    entry: currentEntry,
    setEntry,
    setEntryUpdateVisible,
    //
    task,
    setTasksReadVisible,
    setTaskCreateVisible,
    setTaskKind,
    taskDeleteVisible,
    setTaskDeleteVisible,
    taskDoneVisible,
    setTaskDoneVisible,
    selectedRows,
    setSelectedRows,
    //
    setEntryDeleteVisible,
    setEntryConvertVisible,
    setEntryDetailsVisible,
    setEntryCreateVisible,
    setKanbanCardCreateVisible,
    setSelectedEntryForKanbanCard,
    setEntryAssignVisible,
    //
    setDuplicateEntries,
    setDuplicateEntryVisible,
    setDuplicateEntriesGroups,
    //
    setCurrentOnIgnoreAction,
    //
    setSubToUnlock,
    //
    setImportExportVisible,
    setDuplicateEntryInitiateResolveVisible,
    setTagsManageVisible,
  } = useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const forms = useAppSelector(formSliceSelectors.selectForms);
  const entries = useAppSelector(entrySliceSelectors.selectLatestEntries);
  const entriesMeta = useAppSelector(
    entrySliceSelectors.selectLatestEntriesMeta,
  );
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  const entriesPage = useAppSelector(
    entrySliceSelectors.selectEntriesTablePage,
  );
  const entriesPageSize = useAppSelector(
    entrySliceSelectors.selectEntriesTablePageSize,
  );
  const entriesLoading = useAppSelector(
    entrySliceSelectors.selectLatestEntriesLoading,
  );
  const userPermissions = useAppSelector(userSliceSelectors.selectLoggedInUser)!
    .permissions.permissions;

  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const moduleDefinitions = useAppSelector(
    moduleSliceSelectors.selectAllModuleDefinitions,
  );
  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const whatsAppMetaIntegration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );

  const canShowAssignButton = user?.roles?.includes(UserRoles.ORGANIZATION);
  const canCreateModule = !user?.roles?.includes(UserRoles.MEMBER);
  // A member writes from the inbox their organization's owner set up, and the
  // integration itself is not theirs to reach from here
  const canStartWhatsappChat = !isMember && Boolean(whatsAppMetaIntegration);

  const previewEntryId = searchParams.get("previewEntryId");

  const deleteEntryWarning = useCallback(
    (entry: Entry) => {
      setEntry(entry);
      setEntryDeleteVisible(true);
    },
    [setEntry, setEntryDeleteVisible],
  );

  const convertEntryWarning = useCallback(
    (entry: Entry) => {
      setEntry(entry);
      setEntryConvertVisible(true);
    },
    [setEntry, setEntryConvertVisible],
  );

  const onShowEntryDetails = useCallback(
    (entry: Entry) => {
      setEntry(entry);
      setEntryDetailsVisible(true);
    },
    [setEntry, setEntryDetailsVisible],
  );

  const onCreateDeal = useCallback(
    (entry: Entry) => {
      setEntry(entry);
      setSelectedEntryForKanbanCard(entry);
      setKanbanCardCreateVisible(true);
    },
    [setEntry, setSelectedEntryForKanbanCard, setKanbanCardCreateVisible],
  );

  const updateEntry = useCallback(
    (entry: Entry) => {
      setEntry(entry);
      setEntryUpdateVisible(true);
    },
    [setEntry, setEntryUpdateVisible],
  );

  const updateEntryForCustomField = useCallback(
    async (dto: Partial<Entry>) => {
      const { _id, ownerId, kind, description, ...rest } = dto;

      const updateDto: UpdateEntryDto = {
        userId: ownerId!,
        entryId: _id!,
        fields: rest.fields!,
        description,
      };

      try {
        dispatch(entryActions.setCustomEntryLoadingId(_id));
        await dispatch(entryActions.updateEntryById(updateDto)).unwrap();
        await dispatch(
          entryActions.getEntryById({ entryId: _id!, userId: ownerId! }),
        ).unwrap();
        AtiraToast.success(t("entries.update.success"));
        return true;
      } catch (e: any) {
        console.log(e);
        entryCreateEditDuplicateErrorHandler(
          { error: e },
          {
            setEntries: setDuplicateEntries,
            setGroups: setDuplicateEntriesGroups,
            setModal: setDuplicateEntryVisible,
          },
        );
        setCurrentOnIgnoreAction(() => async () => {
          await dispatch(
            entryActions.updateEntryById({
              ...updateDto,
              ignoreDuplicates: true,
            }),
          );
          await dispatch(
            entryActions.getEntryById({ entryId: _id!, userId: ownerId! }),
          );
        });

        return false;
      } finally {
        dispatch(entryActions.setCustomEntryLoadingId(null));
      }
    },
    [
      dispatch,
      setCurrentOnIgnoreAction,
      setDuplicateEntries,
      setDuplicateEntriesGroups,
      setDuplicateEntryVisible,
      t,
    ],
  );

  const createContactTask = useCallback(
    (entry: Entry) => {
      setEntry(entry);
      setTaskKind(TaskKind.CONTACT);
      setTaskCreateVisible(true);
    },
    [setEntry, setTaskCreateVisible, setTaskKind],
  );

  const createLeadTask = useCallback(
    (entry: Entry) => {
      setEntry(entry);
      setTaskKind(TaskKind.LEAD);
      setTaskCreateVisible(true);
    },
    [setEntry, setTaskCreateVisible, setTaskKind],
  );

  const createModuleInstance = useCallback(
    ({ entry, module }: { entry: Entry; module: ModuleDefinition }) => {
      setEntry(entry);
      setSelectedModuleDefinition(module);
      setModuleInstanceCreateVisible(true);
    },
    [setEntry, setModuleInstanceCreateVisible],
  );

  const attachModuleInstance = useCallback(
    ({ entry, module }: { entry: Entry; module: ModuleDefinition }) => {
      setEntry(entry);
      setSelectedModuleDefinition(module);
      setFixedModuleInstanceAttachToEntryModalVisible(true);
    },
    [setEntry, setFixedModuleInstanceAttachToEntryModalVisible],
  );

  const readTasks = useCallback(
    (entry: Entry) => {
      setEntry(entry);

      dispatch(
        entryActions.getEntryById({
          entryId: entry._id,
          userId,
          ...omitBy(formMethods.getValues(), isNil),
        }),
      );

      setTasksReadVisible(true);
    },
    [dispatch, formMethods, setEntry, setTasksReadVisible, userId],
  );

  const deleteTask = async () => {
    try {
      setDeleteTaskLoading(true);

      await dispatch(
        taskActions.deleteTask({
          taskId: task?._id!,
          userId,
          kind: task!.kind,
        }),
      ).unwrap();

      const updatedEntry = await dispatch(
        entryActions.getEntryById({ entryId: currentEntry?._id!, userId }),
      ).unwrap();

      setEntry(updatedEntry);

      setTaskDeleteVisible(false);

      AtiraToast.success(t("tasks.delete.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setDeleteTaskLoading(false);
    }
  };

  const onPageChange = useCallback(
    async (page: number) => {
      dispatch(entryActions.setEntriesTablePage(page));
    },
    [dispatch],
  );

  const onSelectedRowKeysChange = useCallback(
    (rows: React.Key[]) =>
      setSelectedRows((_prev) => Array.from(new Set(rows))),
    [setSelectedRows],
  );

  const onTaskDone = async () => {
    try {
      if (!task) {
        return;
      }

      setTaskDoneLoading(true);

      await dispatch(
        taskActions.updateTask({
          userId,
          taskId: task._id,
          done: !task.done,
          kind: task.kind,
        }),
      ).unwrap();

      const updatedEntry = await dispatch(
        entryActions.getEntryById({ entryId: currentEntry?._id!, userId }),
      ).unwrap();

      setEntry(updatedEntry);

      setTaskDoneVisible(false);

      AtiraToast.success(
        t(!task?.done ? "tasks.done.success" : "tasks.undone.success"),
      );
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setTaskDoneLoading(false);
    }
  };

  const onTableShowSize = useCallback(
    (curr: number, newSize: number) => {
      dispatch(entryActions.setEntriesTablePageSize(newSize));
      onPageChange(1);
    },
    [dispatch, onPageChange],
  );

  const retrieveEntry = useCallback(
    async (entry: Entry | Partial<Entry>) => {
      const dto = { userId, entryId: entry?._id! };

      try {
        await dispatch(entryActions.retrieveEntry(dto)).unwrap();

        await dispatch(
          entryActions.getEntryById({ entryId: entry?._id!, userId }),
        ).unwrap();

        await dispatch(
          entryActions.getLatestEntries({
            userId,
            meta: {
              count: entriesPageSize,
              page: entriesPage - 1,
            },
            ...{
              ...omitBy(formMethods.getValues(), isNil),
              assigneeIds: formMethods.getValues().assigneeIds?.filter(Boolean),
            },
          }),
        ).unwrap();

        AtiraToast.success(t("tasks.retrieve.success"));
      } catch (e) {
        console.log(e);
        entryCreateEditDuplicateErrorHandler(
          { error: e },
          {
            setEntries: setDuplicateEntries,
            setGroups: setDuplicateEntriesGroups,
            setModal: setDuplicateEntryVisible,
          },
        );

        setCurrentOnIgnoreAction(() => async () => {
          await dispatch(
            entryActions.retrieveEntry({
              userId,
              entryId: entry._id!,
              ignoreDuplicates: true,
            }),
          ).unwrap();
        });
      }
    },
    [
      dispatch,
      entriesPage,
      entriesPageSize,
      formMethods,
      setCurrentOnIgnoreAction,
      setDuplicateEntries,
      setDuplicateEntriesGroups,
      setDuplicateEntryVisible,
      t,
      userId,
    ],
  );

  const onAssignEntry = useCallback(
    (entry: Entry) => {
      setEntry(entry);
      setEntryAssignVisible(true);
    },
    [setEntry, setEntryAssignVisible],
  );

  const onToggleTag = useCallback(
    async (entry: Entry, tagId: string) => {
      const hasTag = entry.tagIds?.includes(tagId);

      try {
        await dispatch(
          entryActions.updateEntryTags({
            userId,
            entryId: entry._id,
            ...(hasTag ? { remove: [tagId] } : { add: [tagId] }),
          }),
        ).unwrap();
        await dispatch(
          entryActions.getEntryById({ entryId: entry._id, userId }),
        ).unwrap();
      } catch (e) {
        console.log(e);
        AtiraToast.apiError(e);
      }
    },
    [dispatch, userId],
  );

  const isSavingColumns = useRef(false);

  // Until the settings are in there's nothing to add to - the "+" would find no
  // hidden custom fields and wrongly report the plan's limit
  const canEditColumns = !isMember && Boolean(tableSettings);

  // "assignee" only exists on the client, it's never saved
  const savedTableColumns = useMemo(
    () =>
      (tableSettings?.columns || []).filter(
        ({ field }) => field !== "assignee",
      ),
    [tableSettings?.columns],
  );

  /** @returns whether the columns were saved */
  const saveTableColumns = useCallback(
    async (columns: typeof savedTableColumns): Promise<boolean> => {
      if (isSavingColumns.current) {
        return false;
      }

      try {
        isSavingColumns.current = true;

        await dispatch(
          entryActions.updateUserTableSettings({
            userId,
            tableId: tableSettings?._id!,
            columns,
          }),
        ).unwrap();
        dispatch(entryActions.setEntryTableSettingsColumns(columns));

        return true;
      } catch (e: any) {
        console.log(e);
        AtiraToast.apiError(e);

        return false;
      } finally {
        isSavingColumns.current = false;
      }
    },
    [dispatch, tableSettings?._id, userId],
  );

  // Every custom field the plan grants already exists on the table settings,
  // just hidden - adding a column reveals the next one. Once they're all
  // showing there's nothing left to add, which is a plan limit.
  const onAddColumn = useCallback(async (): Promise<boolean> => {
    const nextCustomColumn = minBy(
      savedTableColumns.filter(({ custom, enabled }) => custom && !enabled),
      ({ field }) => Number(field.split("-").pop()),
    );

    if (!nextCustomColumn) {
      setSubToUnlock({
        visible: true,
        title: t("subToUnlock.specific.entriesTableColumnsCount"),
      });
      return false;
    }

    // Last in the list, so it lands right where the "+" was
    return saveTableColumns([
      ...savedTableColumns.filter(
        ({ field }) => field !== nextCustomColumn.field,
      ),
      { ...nextCustomColumn, enabled: true },
    ]);
  }, [savedTableColumns, saveTableColumns, setSubToUnlock, t]);

  const onRenameColumn = useCallback(
    (fieldToRename: string, label: string) =>
      saveTableColumns(
        savedTableColumns.map((column) =>
          column.field === fieldToRename ? { ...column, label } : column,
        ),
      ),
    [savedTableColumns, saveTableColumns],
  );

  const onHideColumn = useCallback(
    (fieldToHide: string) =>
      saveTableColumns(
        savedTableColumns.map((column) =>
          column.field === fieldToHide ? { ...column, enabled: false } : column,
        ),
      ),
    [savedTableColumns, saveTableColumns],
  );

  const onStartWhatsappChat = useCallback(
    async (entry: Entry) => {
      const phone = entry.fields?.phone;

      if (!phone || !whatsAppMetaIntegration?._id || whatsappChatLoading) {
        return;
      }

      try {
        setWhatsappChatLoading(true);

        const chat = await dispatch(
          integrationActions.createWhatsappMetaConversation({
            userId,
            integrationId: whatsAppMetaIntegration._id,
            waId: phone,
            name: entry.fields?.name || entry.fields?.email || "",
          }),
        ).unwrap();

        // The inbox opens the chat named in the URL by looking it up in the
        // list it holds, and a chat started a moment ago can be missing from
        // the page the server sends back. A no-op when it is already there.
        dispatch(integrationActions.metaInsertConversation(chat));

        navigate(`/integration/whatsapp-meta?chat=${chat._id}`);
      } catch (e) {
        console.log(e);
        AtiraToast.apiError(e);
      } finally {
        setWhatsappChatLoading(false);
      }
    },
    [
      dispatch,
      navigate,
      userId,
      whatsAppMetaIntegration?._id,
      whatsappChatLoading,
    ],
  );

  const pagination = useMemo(
    () => ({
      pageSizeOptions: ["5", "10", "20", "50"],
      pageSize: entriesPageSize,
      current: entriesPage,
      total: entriesMeta.total,
      onChange: onPageChange,
      showSizeChanger: true,
      onShowSizeChange: onTableShowSize,
      placement: ["bottomCenter"] as TablePaginationConfig["placement"],
    }),
    [
      entriesMeta.total,
      entriesPage,
      entriesPageSize,
      onPageChange,
      onTableShowSize,
    ],
  );

  const tableColumns = useMemo(() => {
    return createEntriesTableColumns(
      tableSettings?.columns || [],
      {
        delete: deleteEntryWarning,
        convert: convertEntryWarning,
        details: onShowEntryDetails,
        deal: onCreateDeal,
        createContactTask,
        createLeadTask,
        readTasks,
        t,
        update: updateEntry,
        updateForCustomField: updateEntryForCustomField,
        assign: canShowAssignButton ? onAssignEntry : undefined,
        startWhatsappChat: canStartWhatsappChat
          ? onStartWhatsappChat
          : undefined,
        toggleTag: onToggleTag,
        manageTags: () => setTagsManageVisible(true),
        retrieve: (entry) =>
          Boolean(entry.markedForDeletion) ? retrieveEntry(entry) : undefined,
        createModuleInstance: canCreateModule
          ? ({ entry, module }) => createModuleInstance({ entry, module })
          : undefined,
        attachModuleInstance: canCreateModule
          ? ({ entry, module }) => attachModuleInstance({ entry, module })
          : undefined,
      },
      { permissions: userPermissions, user, members, moduleDefinitions },
    );
  }, [
    attachModuleInstance,
    canCreateModule,
    canShowAssignButton,
    canStartWhatsappChat,
    convertEntryWarning,
    createContactTask,
    createLeadTask,
    createModuleInstance,
    deleteEntryWarning,
    members,
    moduleDefinitions,
    onAssignEntry,
    onCreateDeal,
    onShowEntryDetails,
    onStartWhatsappChat,
    onToggleTag,
    readTasks,
    retrieveEntry,
    setTagsManageVisible,
    t,
    tableSettings?.columns,
    updateEntry,
    updateEntryForCustomField,
    user,
    userPermissions,
  ]) as TableProps["columns"];

  const subHeaderActions: MenuProps["items"] = useMemo(() => {
    const onFindDuplicates = async () => {
      if (findDuplicatesLoading) {
        return;
      }

      try {
        setFindDuplicatesLoading(true);
        AtiraToast.info(t("entries.findDuplicates.loading.description"), {
          key: FIND_DUPLICATES_TOAST_KEY,
          message: t("entries.findDuplicates.loading.title"),
          icon: <FontAwesomeIcon icon={faSpinner} spin />,
          duration: 0,
        });
        const result = await dispatch(
          entryActions.findPossibleDuplicates({ userId }),
        ).unwrap();
        setDuplicateEntriesGroups(result.groups);

        if (result.entries?.length) {
          AtiraToast.success(
            t("entries.findDuplicates.full", { count: result.entries.length }),
            { key: FIND_DUPLICATES_TOAST_KEY },
          );
          setDuplicateEntryInitiateResolveVisible(true);
        } else {
          AtiraToast.info(t("entries.findDuplicates.empty"), {
            key: FIND_DUPLICATES_TOAST_KEY,
          });
        }
      } catch (e: any) {
        console.log(e);
        AtiraToast.apiError(e, { key: FIND_DUPLICATES_TOAST_KEY });
      } finally {
        setFindDuplicatesLoading(false);
      }
    };

    const actions = [
      {
        key: "create",
        hidden: userPermissions.ENTRY.CREATE !== true,
        onClick: () => setEntryCreateVisible(true),
        "data-testid": TestIDs.entries.createEdit.dropdown.option("create"),
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faPlus} color="main" />
            <Text color="main">{t("common.create")}</Text>
          </Flex>
        ),
      },
      {
        key: "settings",
        hidden: isMember === true,
        onClick: () => setTableSettingsDrawerVisible(true),
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faGear} color="main" />
            <Text color="main">{t("common.settings")}</Text>
          </Flex>
        ),
      },
      {
        key: "export_import",
        "data-testid":
          TestIDs.entries.createEdit.dropdown.option("export_import"),
        onClick: () => setImportExportVisible(true),
        hidden: isMember === true,
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faFile} color="main" />
            <Text color="main">
              {t("common.import")}/{t("common.export")}
            </Text>
          </Flex>
        ),
      },
      {
        key: "find_duplicates",
        onClick: onFindDuplicates,
        hidden: isMember === true,
        "data-testid":
          TestIDs.entries.createEdit.dropdown.option("find_duplicates"),
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faClone} color="main" />
            <Text color="main">{t("common.findDuplicates")}</Text>
          </Flex>
        ),
      },
    ].filter((action) => !action.hidden);

    return actions;
  }, [
    userPermissions.ENTRY.CREATE,
    forms?.length,
    t,
    isMember,
    findDuplicatesLoading,
    dispatch,
    userId,
    setDuplicateEntriesGroups,
    setDuplicateEntryInitiateResolveVisible,
    setEntryCreateVisible,
    setImportExportVisible,
  ]);

  useEffect(() => {
    dispatch(entryActions.getUserTableSettings({ userId }));
    dispatch(kanbanActions.getUserKanban({ userId, kanbanId: kanban?._id }));
    dispatch(formActions.getForms({ userId }));
    dispatch(permissionActions.getMembers({ userId }));
    dispatch(moduleActions.getAllModuleDefinitions({ userId }));
    dispatch(tagActions.getTags());
  }, [userId, dispatch, kanban?._id]);

  useEffect(() => {
    if (isMember) {
      return;
    }

    dispatch(integrationActions.getUserIntegrations({ userId }));
  }, [dispatch, isMember, userId]);

  useEffect(() => {
    if (state?.useFilter) {
      const { form = {} as Form, range, scope, kind } = state.useFilter;
      formMethods.setValue("formId", form?._id);
      formMethods.setValue("range", range);
      // Arriving from a report filtered by a custom date span: without this the
      // list falls back to the default window and contradicts the figure clicked.
      formMethods.setValue("scope", scope);
      formMethods.setValue("kind", kind);
      window.history.replaceState({ ...state, useFilter: undefined }, "");
    }
    dispatch(
      entryActions.getLatestEntries({
        userId,
        ...{
          ...omitBy(formMethods.getValues(), isNil),
          assigneeIds: formMethods.getValues().assigneeIds?.filter(Boolean),
        },
        meta: PageMeta.create({
          count: entriesPageSize,
          page: entriesPage - 1,
        }),
      }),
    );
  }, [dispatch, entriesPage, entriesPageSize, formMethods, state, userId]);

  useEffect(() => {
    if (!previewEntryId || !OBJECT_ID_REGEX.test(previewEntryId)) {
      return;
    }

    const fetchEntry = async () => {
      try {
        const entry = await dispatch(
          entryActions.getEntryById({ entryId: previewEntryId, userId }),
        ).unwrap();

        setEntry(entry);
        setEntryDetailsVisible(true);
      } catch (e) {
        console.log(e);
      }
    };

    fetchEntry();
  }, [dispatch, userId, previewEntryId, setEntryDetailsVisible, setEntry]);

  return (
    <FormProvider {...formMethods}>
      <Flex flexDirection="column" overflow="visible">
        <SubHeader
          icon={EntityIcons.Entries}
          title={
            <Trans
              i18nKey="subheader.entries"
              components={[
                <Badge
                  style={{ marginBlockEnd: Spacing.s }}
                  count={entriesMeta.total || 0}
                  color={theme.main}
                  overflowCount={999999}
                />,
              ]}
            />
          }
          extra={
            subHeaderActions.length ? (
              <AtiraDropdown
                items={subHeaderActions}
                placement="bottomRight"
                trigger={["click"]}
                dataTestId={TestIDs.entries.createEdit.dropdown.open}
              >
                <Button
                  icon={faBars}
                  color="black"
                  padding="s"
                  backgroundColor="white"
                  hoverBackgroundColor="lightMain"
                  hover
                  loading={findDuplicatesLoading}
                  loadingColor="main"
                >
                  {t("common.options")}
                </Button>
              </AtiraDropdown>
            ) : null
          }
        />

        <EntryTableHeader showFilters={!Boolean(selectedRows.length)} />
        <Wrapper>
          {selectedRows.length ? <EntryTableBulkActionsHeader /> : null}

          <EntiresTableWithDND
            key={tableSettings?._id}
            columns={tableColumns?.length ? tableColumns : []}
            data={entries}
            pagination={pagination}
            selectedRowKeys={selectedRows}
            onSelectedRowKeysChange={onSelectedRowKeysChange}
            style={{ opacity: entriesLoading || whatsappChatLoading ? 0.5 : 1 }}
            onAddColumn={canEditColumns ? onAddColumn : undefined}
            onHideColumn={canEditColumns ? onHideColumn : undefined}
            onRenameColumn={canEditColumns ? onRenameColumn : undefined}
          />

          <WarningModal
            isOpen={taskDeleteVisible}
            loading={deleteTaskLoading}
            onConfirm={deleteTask}
            onClose={() => setTaskDeleteVisible(false)}
            title={t("tasks.delete.warning.title", {
              task_name: truncateString(task?.name || "", 26),
            })}
            description={t("tasks.delete.warning.description")}
          />

          <WarningModal
            isOpen={taskDoneVisible}
            onClose={() => setTaskDoneVisible(false)}
            title={t(
              task?.done === true
                ? "tasks.undone.modal.title"
                : "tasks.done.modal.title",
            )}
            description={t(
              task?.done === true
                ? "tasks.undone.modal.description"
                : "tasks.done.modal.description",
            )}
            onConfirm={onTaskDone}
            loading={taskDoneLoading}
          />

          <EntryTableSettingsDrawer
            open={tableSettingsDrawerVisible}
            onClose={() => setTableSettingsDrawerVisible(false)}
          />

          <DynamicModuleInstanceCreateDrawer
            open={moduleInstanceCreateVisible}
            onClose={() => setModuleInstanceCreateVisible(false)}
            moduleDefinition={selectedModuleDefinition}
            entry={currentEntry}
          />

          <ModuleInstanceAttachmentCreateModal
            open={fixedModuleInstanceAttachToEntryModalVisible}
            onClose={() =>
              setFixedModuleInstanceAttachToEntryModalVisible(false)
            }
            moduleDefinition={selectedModuleDefinition}
            entry={currentEntry}
          />

          <EntriesDuplicatesResolveDrawer />
          <EntryDuplicateBulkInitiateResolveDrawer />
        </Wrapper>
      </Flex>

      <ExportImportEntriesModal />
    </FormProvider>
  );
};

export default EntriesRoute;
